<script>
import {defineComponent} from "vue";
import {ApplicationHelper} from "@/javascript/helpers/ApplicationHelper";
import {useLandingPageStore} from "../../stores/landingPage";

export default defineComponent({
  components: {},
  props: {
    /**
     * The total sum of the donations to the campaign.
     */
    totalDonations: {
      type: Number,
      default: 0
    },
    /**
     * The campaign donation goal.
     */
    donationTarget: {
      type: Number,
      default: 0
    },

    /**
     * The base color of the unfilled donation bar.
     */
    backgroundColor: {
      type: String,
      default: "#F3F2F2"
    },
    /**
     * The color of the filled portion of the donation bar.
     */
    fillColor: {
      type: String,
      default: "#085E2DFF"
    },

    size: {
      type: String,
      default: "large"
    }
  },

  setup() {
    const landingStore = useLandingPageStore();
    return { landingStore };
  },
  data() {
    return {
      isExpanded: false
    };
  },

  computed: {
    donationBarFillWidth() {
      if (this.totalDonations >= this.donationTarget) {
        return '100%';
      } else if (this.totalDonations === 0 || this.donationTarget === 0) {
        return '0%';
      } else {
        let percent = Math.round((this.totalDonations / this.donationTarget) * 100);
        return `${percent}%`;
      }
    },
    effectiveFillColor() {
      // If fillColor has good contrast against the background, use it.
      // Otherwise, fall back to brand's primary color.
      const contrastTextColor = this.landingStore.getContrastTextColor(this.fillColor);
      const backgroundContrastTextColor = this.landingStore.getContrastTextColor(this.backgroundColor);

      // If the contrast text color for the fillColor is not similar to background, we can use it
      return contrastTextColor === backgroundContrastTextColor
        ? this.landingStore.localBrand.color_primary
        : this.fillColor;
    },
    cssProps() {
      return {
        '--donation-bar-base-color': this.backgroundColor,
        '--donation-bar-fill-color': this.effectiveFillColor,
        '--donation-bar-fill-width': this.donationBarFillWidth,
        '--donation-bar-height': this.size === "large" ? "60px" : "8px",
        '--donation-bar-border-width': this.size === "large" ? "3px" : "0"
      };
    },
    formattedEndDate() {
      if (!this.endDate) return null;

      const date = new Date(this.endDate);
      const options = {month: 'short', day: 'numeric'};
      const formattedDate = new Intl.DateTimeFormat('en-US', options).format(date);

      // Add "th", "st", "nd", or "rd" based on the day of the month
      const day = date.getDate();

      return `Ends ${formattedDate}`;
    }
  },
  mounted() {
    //TODO don't do on mount, do when campaign loads;
    setTimeout(() => {
      this.isExpanded = true;
    }, 1500);
  },
  methods: {
    moneyFormat(val) {
      return ApplicationHelper.formatMoney(val, true, '$0');
    },
  }
});

</script>

<template>
  <div
    class="donation-info"
    :style="cssProps"
  >
    <div
      v-if="donationTarget"
      class="donation-bar-container"
    >
      <div class="donation-bar">
        <div
          class="donation-bar-fill"
          :class="{'expanded': isExpanded}"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/styles/_variables.scss';

.donation-bar-container {
  .donation-bar {
    height: var(--donation-bar-height);
    @apply w-full rounded-full;
    background-color: var(--donation-bar-base-color);
    border: var(--donation-bar-border-width) solid var(--donation-bar-fill-color);
    overflow: hidden;

    .donation-bar-fill {
      @apply h-full;
      width: 0;
      background-color: var(--donation-bar-fill-color);
      transition: width 3s ease;

      &.expanded {
        width: var(--donation-bar-fill-width);
      }
    }
  }

  .caption-regular {
    margin-top: $space-xs;

    span {
      font-weight: bold;
    }
  }
}

.fund-donation-total {
  display: flex;
  gap: $space-xs;

  span:first-child {
    color: var(--donation-bar-fill-color);
  }
}
</style>
